import { useNavigate, Navigate, useLocation } from "react-router-dom";
import "../styles/StudentAbsence.css";
import toast from "react-hot-toast";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  fetchStudents,
} from "../services/data";
import { sendStudentAttendance } from "../services/pms";
import { useLanguage } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import LoadingScreen from "../components/LoadingScreen";
import DenyAccessPage from "../components/DenyAccessPage";
import Popup from "../components/Popup";
import Selector2 from "../components/Selector2";
import { fetchWisdomClasses, fetchWisdomRelatedSchools, fetchWisdomStages } from "services/wisdom/wisdomData";
import WisdomNavbar from "components/navbars/WisdomNabvar";
import Uploading from "components/Uploading";

const RENDER_CHUNK = 100;
const MemoSelector = memo(Selector2);
const currentDay = new Date().toISOString().split("T")[0];

// "" = nothing chosen yet, "0" = the "All" option in Selector2
const isUnset = (value) => value === "" || value === "0";

const StudentRow = memo(function StudentRow({ student, showTitles, language }) {
  return (
    <div className="flex flex-col md:flex-row justify-evenly items-center m-[10px] gap-3 md:gap-0 p-4 md:p-0 bg-gray-50 md:bg-transparent border md:border-0 border-gray-200 rounded-xl md:rounded-none shadow-sm md:shadow-none">
      <div className="text-center flex flex-col justify-center items-center w-full md:w-[30%] md:min-w-[200px]">
        <div className={`studentTitle ${!showTitles ? "md:hidden" : ""}`}>
          {language ? "Name:" : ":الاسم"}
        </div>
        <div className="">
          {student.first_name} {student.middle_name} {student.last_name}
        </div>
      </div>
      <div className="text-center flex flex-col justify-center items-center w-full md:w-[30%] md:min-w-[200px]">
        <div className={`studentTitle ${!showTitles ? "md:hidden" : ""}`}>
          {language ? "Status:" : ":الحالة"}
        </div>
        <select
          id={`absenceStatus-${student.id}`}
          name={`absenceStatus-${student.id}`}
          className="w-full py-2.5 px-3 border border-[#ccc] rounded-lg text-sm outline-none transition-[border-color] duration-200 focus:border-[#007bff] focus:shadow-[0_0_0_2px_rgba(0,123,255,0.1)] gap-1 bg-white max-w-[220px] md:w-auto"
        >
          <option value="attend">
            {language ? "Attended" : "حاضر"}
          </option>
          <option value="absent">{language ? "Absent" : "غائب"}</option>
          <option value="late">{language ? "Late" : "متاخر"}</option>
          <option value="left with parent">
            {language ? "Left with parent" : "رحل مع عائلته"}
          </option>
        </select>
      </div>
      <div className="text-center flex flex-col justify-center items-center w-full md:w-[30%] md:min-w-[200px]">
        <div className={`font-bold text-center ${!showTitles ? "md:hidden" : ""}`}>
          {language ? "Reason (optional):" : "السبب (اختياري)"}
        </div>
        <input
          id={`reasonInput ${student.id}`}
          name={`reason-${student.id}`}
          type="text"
          className="py-2.5 px-3 border border-[#ccc] rounded-lg text-sm outline-none transition-[border-color] duration-200 focus:border-[#007bff] focus:shadow-[0_0_0_2px_rgba(0,123,255,0.1)] gap-1 w-full max-w-[220px] md:w-auto"
        />
      </div>
    </div>
  );
});

function StudentAbsence() {
  const location = useLocation();
  const { language } = useLanguage();
  const [submitted, setSubmitted] = useState(false);
  const [students, setStudnets] = useState([]);
  const [unfilteredClasses, setUnfilteredClasses] = useState([]);
  const [stages, setStages] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState("");
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSchool, setSelectedSchool] = useState("");
  const [selectedStage, setSelectedStage] = useState("");
  const [fetching, setFetching] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const { userInfo } = useAuth();
  const navigate = useNavigate();
  const [selectedDay, setSelectedDay] = useState(currentDay);

  // fetching data
  useEffect(() => {
    const loadData = async () => {
      try {
        setFetching(true);
        if (userInfo?.user_role === "Operations Excellence Lead" || userInfo?.user_role === "Super Admin") {
          const schoolResponse = await fetchWisdomRelatedSchools();
          setSchools(schoolResponse);
        }
        const [stagesResponse, classesResponse, studentsResponse] = await Promise.all([
          fetchWisdomStages(),
          fetchWisdomClasses(),
          fetchStudents()
        ]);
        setStages(stagesResponse);
        setUnfilteredClasses(classesResponse);
        setStudnets(studentsResponse);
      } catch (err) {
        console.error("API Error:", err);
        setError(err);
      } finally {
        setFetching(false);
      }
    };

    loadData();
  }, [userInfo]);

  const isAdmin = userInfo?.user_role === "Operations Excellence Lead" || userInfo?.user_role === "Super Admin";

  const createTimestamp = () => {
    const today = new Date();

    // Extract current time components
    const hh = String(today.getHours()).padStart(2, "0");
    const mm = String(today.getMinutes()).padStart(2, "0");
    const ss = String(today.getSeconds()).padStart(2, "0");
    const ms = String(today.getMilliseconds()).padStart(2, "0");

    // selectedDay = "2025-09-19"
    return `${selectedDay} ${hh}:${mm}:${ss}.${ms}+00`;
  };

  const handleStudent = useCallback((e) => {
    setSelectedStudent(e.target.value);
  }, []);

  const handleClass = useCallback((e) => {
    setSelectedClass(e.target.value);
    setSelectedStudent("");
  }, []);

  const handleSchool = useCallback((e) => {
    setSelectedSchool(e.target.value);
    setSelectedClass("");
    setSelectedStudent("");
  }, []);

  const handleStage = useCallback((e) => {
    setSelectedStage(e.target.value);
    setSelectedClass("");
    setSelectedStudent("");
  }, []);

  const submitStudentAttendance = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const attendanceData = selectedStudents.map((student) => ({
      student_id: student.id,
      status: formData.get(`absenceStatus-${student.id}`),
      reason: formData.get(`reason-${student.id}`),
      createdAt: createTimestamp(),
      updatedAt: createTimestamp()
    }));

    try {
      setSubmitting(true);
      await sendStudentAttendance(attendanceData);
      toast.success(language ? "Attendance has been submitted" : "تم تسجيل الحضور");
      setSubmitted(true);
    } catch (err) {
      toast.error("please fill all data required");
    } finally {
      setSubmitting(false);
    }
  };

  const classes = useMemo(() => {
    const organizationId = String((isAdmin ? selectedSchool : userInfo?.organization_id) ?? "");
    return unfilteredClasses.filter((filterClass) =>
      (isUnset(selectedStage) || filterClass.stage_id === Number(selectedStage)) &&
      (isUnset(organizationId) || filterClass.classRoom?.organization_id === Number(organizationId))
    );
  }, [selectedStage, selectedSchool, unfilteredClasses, userInfo, isAdmin]);

  // Students matching school / grade / class (feeds both the student dropdown and the list)
  const filteredStudents = useMemo(() => {
    const classIds = new Set(classes.map((filterClass) => filterClass.id));
    return students.filter((student) =>
      (isUnset(selectedSchool) || student.school_id === Number(selectedSchool)) &&
      (isUnset(selectedStage) || classIds.has(student.class_id)) &&
      (isUnset(selectedClass) || student.class_id === Number(selectedClass))
    );
  }, [students, classes, selectedSchool, selectedStage, selectedClass]);

  const selectedStudents = useMemo(() => {
    const nothingSelected = [selectedSchool, selectedStage, selectedClass, selectedStudent].every((value) => value === "");
    if (nothingSelected) return [];
    return filteredStudents.filter((student) => isUnset(selectedStudent) || student.id === Number(selectedStudent));
  }, [filteredStudents, selectedSchool, selectedStage, selectedClass, selectedStudent]);

  // Render the list in chunks so the page (and the loading spinner) never freezes on big lists
  const [renderedList, setRenderedList] = useState(selectedStudents);
  const [visibleCount, setVisibleCount] = useState(0);
  if (renderedList !== selectedStudents) {
    setRenderedList(selectedStudents);
    setVisibleCount(0);
  }
  const rendering = visibleCount < selectedStudents.length;

  useEffect(() => {
    if (!rendering) return;
    const timer = setTimeout(() => setVisibleCount((count) => count + RENDER_CHUNK), 0);
    return () => clearTimeout(timer);
  }, [rendering, visibleCount]);

  const closePopup = () => {
    setSubmitted(false)
    navigate('/pms');
  };


  if (fetching) return <LoadingScreen />;
  if (error?.status === 403) return <Navigate to="/login" state={{ from: location }} replace />;
  if (error) return <p>Error: {error.message}</p>;
  if (userInfo.user_role !== "Operations Excellence Lead" && userInfo.user_role !== "Supervisor" && userInfo.user_role !== "Social Worker") return <DenyAccessPage homePage='/pms' />;
  if (userInfo?.code === 1452 || userInfo?.code === 1476) return <DenyAccessPage homePage='/watoms/dashboard' />;
  if (userInfo?.code === 1475) return <DenyAccessPage homePage='/watoms/news' />;
  if (userInfo?.code === 1310) return <DenyAccessPage homePage='/wisdom/dashboard' />;

  return (
    <div className="bg-formColor flex justify-center flex-wrap min-h-screen gap-1">
      <WisdomNavbar />
      {(rendering || submitting) && <Uploading />}
      <div className="w-full grid grid-cols-1 md:grid-cols-2 gap-4 max-w-4xl mx-auto" dir={language ? "ltr" : "rtl"}>
        {/* vtc selector */}
        <div className="flex flex-col justify-center items-center" dir="rtl">
            <label className="font-bold m-[5px] text-center">اليوم:</label>
            <input
              className="w-60 p-2"
              type="date"
              value={selectedDay}
              onChange={(e) => setSelectedDay(e.target.value)}
            />
          </div>
        {userInfo.user_role === "Operations Excellence Lead" ? <MemoSelector
          label="school"
          title={language ? "School:" : "مدرسة:"}
          description={language ? "Please Select a school" : "الرجاء اختيار مدرسة"}
          data={schools}
          value={selectedSchool}
          onChange={handleSchool}
          name="name"
          extraCSS="w-[99%]"
          All={true}
        /> : null}
        <MemoSelector
          label="grade"
          title={language ? "Grade:" : "مرحلة:"}
          description={language ? "Please Select a grade" : "الرجاء اختيار مرحلة"}
          data={stages}
          value={selectedStage}
          onChange={handleStage}
          name="name"
          extraCSS="w-[99%]"
          All={true}
        />
        <MemoSelector
          label="class"
          title={language ? "Class:" : "فصل:"}
          description={language ? "Please Select a class" : "الرجاء اختيار فصل"}
          data={classes}
          value={selectedClass}
          onChange={handleClass}
          name="name"
          extraCSS="w-[99%]"
          All={true}
        />
        <MemoSelector
          label="student"
          title={language ? "Student:" : "طالب:"}
          description={language ? "Please Select a student" : "الرجاء اختيار طالب"}
          data={filteredStudents}
          value={selectedStudent}
          onChange={handleStudent}
          name="userStd"
          extraCSS="w-[99%]"
          All={true}
        />
      </div>
      {selectedStudents.length > 0 ? (
        <form className="bg-white max-w-[90%] w-full mx-auto p-2.5 rounded-[5px] shadow-[0_4px_10px_rgba(0,0,0,0.1)] h-auto overflow-x-auto [-webkit-overflow-scrolling:touch]" onSubmit={submitStudentAttendance}>
          {selectedStudents.slice(0, visibleCount).map((student, idx) => (
            <StudentRow key={student.id} student={student} showTitles={idx === 0} language={language} />
          ))}
          <div className="flex justify-center items-center w-full">
            <button className="bg-wisdomOrange hover:bg-wisdomDarkOrange text-white rounded p-2 flex self-center items-center">
              {language ? "Submit" : "ارسال"}
            </button>
          </div>
        </form>
      ) : (
        <div className="noTeacher">
          {language ? "No Data Available" : "لا يوجد بيانات حاليا"}
        </div>
      )}
      <Popup
        isOpen={submitted}
        onClose={closePopup}
        message={language ? "Attendance has been submitted successfully" : "تم تسجيل الحضور بنجاح"}
      />
    </div>
  );
}

export default StudentAbsence;
